import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { Search, Filter } from 'lucide-react';
import { fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

export default function AdminAuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [memberFilter, setMemberFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (action) params.action = action;
      if (memberFilter) params.member_user_id = memberFilter;
      setRows(await api.adminAuditLogs(params));
    } catch { toast.error('Could not load audit logs'); }
    finally { setLoading(false); }
  }, [action, memberFilter]);

  useEffect(() => { load(); }, [load]);

  const actions = Array.from(new Set(rows.map((r) => r.action))).sort();

  return (
    <AdminLayout title="Audit Log" subtitle="Sensitive document and account activity, append-only.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <select data-testid="audit-action" value={action} onChange={(e) => setAction(e.target.value)}
                className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] bg-white">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input data-testid="audit-member" value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)}
               onBlur={load} placeholder="Filter by member user id…"
               className="md:col-span-2 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] bg-white focus:border-[#f5b300] outline-none" />
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">Loading…</p>
          : rows.length === 0 ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">No log entries match.</p>
          : <table className="w-full text-[13px]" data-testid="audit-table">
              <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-5 py-2.5">When</th>
                  <th className="text-left px-5 py-2.5">Actor</th>
                  <th className="text-left px-5 py-2.5">Action</th>
                  <th className="text-left px-5 py-2.5">Target</th>
                  <th className="text-left px-5 py-2.5">Member</th>
                  <th className="text-left px-5 py-2.5">Meta</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-5 py-2.5 text-neutral-600 text-[12px]">{fmtDateTime(r.at)}</td>
                    <td className="px-5 py-2.5">
                      <p className="font-semibold text-[#14110f]">{r.actor_email || '—'}</p>
                      <p className="text-[11px] text-neutral-500">{r.actor_role}</p>
                    </td>
                    <td className="px-5 py-2.5 text-neutral-700">{r.action}</td>
                    <td className="px-5 py-2.5 text-neutral-500 font-mono text-[11px]">{r.target_type}<br />{r.target_id}</td>
                    <td className="px-5 py-2.5 text-neutral-500 font-mono text-[11px]">{r.member_user_id || '—'}</td>
                    <td className="px-5 py-2.5 text-neutral-600 text-[11.5px] font-mono">{r.meta ? JSON.stringify(r.meta) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </AdminLayout>
  );
}
